import { useCallback, useEffect, useRef, useState } from 'react';
import {
  computeIdlePhase,
  DEFAULT_IDLE_TIMEOUT_MS,
  DEFAULT_WARNING_BEFORE_MS,
  formatIdleTabTitle,
  stripIdleTabTitlePrefix,
} from '../utils/idleSessionUtils';
import { performSessionLogout } from '../utils/sessionLogout';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'];

export function useIdleSession({
  enabled = true,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
  warningBeforeMs = DEFAULT_WARNING_BEFORE_MS,
  pageTitle = '',
  onLogout = performSessionLogout,
  now = () => Date.now(),
} = {}) {
  const nowRef = useRef(now);
  const onLogoutRef = useRef(onLogout);
  const pageTitleRef = useRef(pageTitle);
  const tickRef = useRef(() => {});

  nowRef.current = now;
  onLogoutRef.current = onLogout;
  pageTitleRef.current = pageTitle;

  const getNow = () => nowRef.current();
  const lastActivityRef = useRef(getNow());
  const warningOpenRef = useRef(false);
  const secondsRemainingRef = useRef(0);
  const expiredRef = useRef(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const setWarning = useCallback((open, seconds = 0) => {
    if (warningOpenRef.current === open && secondsRemainingRef.current === seconds) return;
    warningOpenRef.current = open;
    secondsRemainingRef.current = seconds;
    setWarningOpen(open);
    setSecondsRemaining(seconds);
  }, []);

  const staySignedIn = useCallback(() => {
    lastActivityRef.current = getNow();
    tickRef.current('stay-signed-in');
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const getPhase = () => computeIdlePhase({
      lastActivityAt: lastActivityRef.current,
      now: getNow(),
      idleTimeoutMs,
      warningBeforeMs,
    });

    let titleTimerId = null;

    const clearTitleTimer = () => {
      if (titleTimerId !== null) {
        clearTimeout(titleTimerId);
        titleTimerId = null;
      }
    };

    const syncTabTitle = (phase) => {
      const base = stripIdleTabTitlePrefix(pageTitleRef.current || document.title);
      if (!base) return;

      if (phase.phase === 'warning') {
        document.title = formatIdleTabTitle(phase.secondsRemaining, base);
        clearTitleTimer();
        const msLeft = lastActivityRef.current + idleTimeoutMs - getNow();
        if (msLeft > 0) {
          const delay = Math.max(200, Math.min(1000, msLeft % 1000 || 1000));
          titleTimerId = window.setTimeout(() => {
            titleTimerId = null;
            const next = getPhase();
            syncTabTitle(next);
            if (next.phase === 'expired') tick('title-timer');
          }, delay);
        }
        return;
      }

      clearTitleTimer();
      document.title = base;
    };

    const tick = () => {
      const phase = getPhase();
      syncTabTitle(phase);

      if (phase.phase === 'expired') {
        if (!expiredRef.current) {
          expiredRef.current = true;
          onLogoutRef.current();
        }
        return;
      }

      if (phase.phase === 'warning') {
        setWarning(true, phase.secondsRemaining);
        return;
      }

      setWarning(false);
    };

    tickRef.current = tick;

    const onActivity = () => {
      if (document.visibilityState !== 'visible' || warningOpenRef.current) return;
      lastActivityRef.current = getNow();
      tick();
    };

    const onVisibilityChange = () => tick();

    document.addEventListener('visibilitychange', onVisibilityChange);
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, onActivity, { capture: true, passive: true });
    });

    tick();
    const intervalId = window.setInterval(tick, 1000);

    return () => {
      clearTitleTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, onActivity, { capture: true });
      });
      window.clearInterval(intervalId);
    };
  }, [enabled, idleTimeoutMs, warningBeforeMs, setWarning]);

  return { warningOpen, secondsRemaining, staySignedIn };
}
